/* Signature - shared product variant state for the PDP and quick view.
   Native product forms remain the no-JavaScript fallback. */
(function (global) {
  'use strict';

  function variantOptions(variant) {
    if (Array.isArray(variant && variant.options)) return variant.options;
    return [variant && variant.option1, variant && variant.option2, variant && variant.option3]
      .filter((value) => value != null);
  }

  function findVariant(variants, options) {
    return variants.find((variant) => {
      const candidate = variantOptions(variant);
      return candidate.length === options.length &&
        candidate.every((value, index) => value === options[index]);
    }) || null;
  }

  function formatMoney(cents, options) {
    const amount = Number(cents) || 0;
    const customFormatter = options && options.formatMoney;
    const shopifyFormatter = global.Shopify && global.Shopify.formatMoney;
    const formatter = customFormatter || shopifyFormatter;

    if (typeof formatter === 'function') {
      return formatter(amount);
    }

    const currency = (options && options.currency) || 'USD';
    const locale = (options && options.locale) || undefined;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount / 100);
    } catch (error) {
      return (amount / 100).toFixed(2) + ' ' + currency;
    }
  }

  function deriveState(variant, options) {
    if (!variant) {
      return {
        valid: false,
        id: '',
        available: false,
        price: '',
        compareAtPrice: '',
        savingsPercent: 0,
        onSale: false,
        sku: '',
        mediaId: '',
      };
    }

    const price = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    const onSale = compareAt > price;

    return {
      valid: true,
      id: String(variant.id),
      available: Boolean(variant.available),
      price: formatMoney(price, options),
      compareAtPrice: onSale ? formatMoney(compareAt, options) : '',
      savingsPercent: onSale ? Math.round(((compareAt - price) / compareAt) * 100) : 0,
      onSale,
      sku: variant.sku || '',
      mediaId: String(
        variant.featured_media_id ||
        (variant.featured_media && variant.featured_media.id) ||
        ''
      ),
    };
  }

  function findTemplate(templates, mediaId) {
    if (!templates || !mediaId) return null;
    return Array.from(templates.querySelectorAll('[data-template-media-id]'))
      .find((template) => template.getAttribute('data-template-media-id') === mediaId) || null;
  }

  function setZoomAvailability(mainMedia) {
    if (!mainMedia || !mainMedia.hasAttribute('data-zoom-container')) return;
    const canZoom = Boolean(mainMedia.querySelector('img'));
    if (canZoom) {
      mainMedia.setAttribute('data-zoom-trigger', '');
      mainMedia.setAttribute('role', 'button');
      mainMedia.setAttribute('tabindex', '0');
      mainMedia.setAttribute('aria-label', mainMedia.getAttribute('data-zoom-label') || 'Zoom image');
    } else {
      mainMedia.removeAttribute('data-zoom-trigger');
      mainMedia.removeAttribute('role');
      mainMedia.removeAttribute('tabindex');
      mainMedia.removeAttribute('aria-label');
    }
  }

  function activateMedia(root, mediaId) {
    if (!mediaId) return false;
    const mainMedia = root.querySelector('[data-main-media]');
    if (!mainMedia) return false;
    if (mainMedia.getAttribute('data-current-media-id') !== mediaId) {
      const templates = root.querySelector('[data-media-templates]');
      const source = findTemplate(templates, mediaId);
      if (!source) return false;
      const content = source.content
        ? source.content.cloneNode(true)
        : source.cloneNode(true);
      mainMedia.replaceChildren(content);
      mainMedia.setAttribute('data-current-media-id', mediaId);
    }
    setZoomAvailability(mainMedia);

    root.querySelectorAll('[data-media-id]').forEach((thumbnail) => {
      const active = thumbnail.getAttribute('data-media-id') === mediaId;
      thumbnail.classList.toggle('is-active', active);
      if (active) {
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });

    const counter = root.querySelector('[data-media-counter]');
    if (counter) {
      const thumbs = Array.from(root.querySelectorAll('[data-media-id]'));
      const index = thumbs.findIndex(
        (thumb) => thumb.getAttribute('data-media-id') === mediaId
      );
      const total = counter.getAttribute('data-media-total');
      if (index >= 0 && total) counter.textContent = (index + 1) + ' / ' + total;
    }
    return true;
  }

  function resetMedia(root) {
    const fallbackMediaId = root.getAttribute('data-fallback-media-id') || '';
    if (fallbackMediaId && activateMedia(root, fallbackMediaId)) return true;

    const mainMedia = root.querySelector('[data-main-media]');
    if (mainMedia) {
      mainMedia.replaceChildren();
      mainMedia.removeAttribute('data-current-media-id');
      setZoomAvailability(mainMedia);
    }
    root.querySelectorAll('[data-media-id]').forEach((thumbnail) => {
      thumbnail.classList.toggle('is-active', false);
      thumbnail.removeAttribute('aria-current');
    });
    return false;
  }

  function updateVariantUrl(url, variantId) {
    const next = new URL(url, global.location && global.location.origin);
    next.searchParams.set('variant', variantId);
    return next.toString();
  }

  function removeVariantUrl(url) {
    const next = new URL(url, global.location && global.location.origin);
    next.searchParams.delete('variant');
    return next.toString();
  }

  function updateStructuredData(script, variant, variantUrl) {
    if (!script || !variant) return;
    try {
      const data = JSON.parse(script.textContent);
      const offer = data.offers || script.__signatureOffer;
      if (!offer) return;
      script.__signatureOffer = { ...offer };
      data.offers = { ...offer };
      data.offers.url = variantUrl;
      data.offers.price = (Number(variant.price) / 100).toFixed(2);
      /*
       * Carry the SELECTED variant's real availability. This used to delete the
       * field, which was right when the server emitted none - but the server now
       * renders availability from Shopify's own `available` flag, and deleting
       * it stripped the one property Google requires on an Offer. Re-derive it
       * rather than leaving the first variant's value in place, which would be
       * stale and could claim stock for a variant that has none.
       */
      if (typeof variant.available === 'boolean') {
        data.offers.availability = variant.available
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock';
      } else {
        delete data.offers.availability;
      }
      if (variant.sku) data.sku = variant.sku;
      else delete data.sku;
      if (variant.barcode) data.gtin = variant.barcode;
      else delete data.gtin;
      script.textContent = JSON.stringify(data);
    } catch (error) {
      /* The server-rendered JSON-LD remains valid when dynamic enhancement fails. */
    }
  }

  function clearStructuredData(script) {
    if (!script) return;
    try {
      const data = JSON.parse(script.textContent);
      if (data.offers) script.__signatureOffer = { ...data.offers };
      delete data.offers;
      delete data.sku;
      delete data.gtin;
      script.textContent = JSON.stringify(data);
    } catch (error) {
      /* The server-rendered JSON-LD remains valid when dynamic enhancement fails. */
    }
  }

  function applyVariant(root, variant, options) {
    const settings = options || {};
    const currency = root.getAttribute('data-currency-code') || settings.currency || 'USD';
    const locale = document.documentElement.lang || undefined;
    const state = deriveState(variant, {
      currency,
      locale,
      formatMoney: settings.formatMoney,
    });

    const idInput = root.querySelector('[data-variant-id]');
    if (idInput) idInput.value = state.id;

    const priceWrap = root.querySelector('[data-price-wrap]');
    if (priceWrap) priceWrap.hidden = !state.valid;
    root.querySelectorAll('[data-current-price]').forEach((currentPrice) => {
      currentPrice.textContent = state.price;
    });
    const compareWrap = root.querySelector('[data-compare-price-wrap]');
    if (compareWrap) compareWrap.hidden = !state.onSale;
    const comparePrice = root.querySelector('[data-compare-price]');
    if (comparePrice) comparePrice.textContent = state.compareAtPrice;
    const savings = root.querySelector('[data-savings]');
    if (savings) {
      savings.hidden = !state.onSale;
      savings.textContent = state.onSale
        ? (savings.getAttribute('data-label-template') || 'Save [percent]%')
          .replace('[percent]', String(state.savingsPercent))
        : '';
    }

    const skuRow = root.querySelector('[data-sku-row]');
    const sku = root.querySelector('[data-variant-sku]');
    if (sku) sku.textContent = state.sku;
    if (skuRow) skuRow.hidden = !state.sku;

    const comboNote = root.querySelector('[data-combo-note]');
    if (comboNote) comboNote.hidden = state.valid;

    const dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
    if (dynamicCheckout) dynamicCheckout.hidden = !(state.valid && state.available);

    const availability = root.querySelector('[data-availability]');
    const availabilityLabel = state.valid
      ? (state.available
        ? availability && availability.getAttribute('data-label-in')
        : availability && availability.getAttribute('data-label-out'))
      : availability && availability.getAttribute('data-label-unavailable');
    if (availability) {
      availability.classList.toggle('is-out', !state.available);
      availability.textContent = availabilityLabel ||
        (state.available ? 'Available to order' : 'Currently unavailable');
    }

    const addButton = root.querySelector('[data-add-button]');
    if (addButton) {
      addButton.disabled = !state.available;
      addButton.textContent = state.valid
        ? (state.available
          ? addButton.getAttribute('data-label-add') || 'Add to cart'
          : addButton.getAttribute('data-label-sold') || 'Currently unavailable')
        : addButton.getAttribute('data-label-unavailable') || 'Currently unavailable';
    }

    root.querySelectorAll('[data-sticky-add]').forEach((stickyAdd) => {
      stickyAdd.disabled = !state.available;
    });

    let variantUrl = '';
    const productUrl = root.getAttribute('data-product-url') ||
      (global.location && global.location.href) ||
      '/';
    if (state.valid) {
      /*
       * HERO ON LOAD, SWAP ON CLICK.
       *
       * The product's featured media - the VF styled hero - is what a shopper
       * must land on, whichever variant the form happens to resolve to first.
       * That includes products whose leading variant is sold out (the form
       * snaps to the first purchasable one, whose own photo would otherwise
       * win) and ?variant= deep links from ads, search and shared URLs.
       *
       * data-hero-initial is set by bind() before the first paint and removed
       * the moment a shopper touches an option control, so a variant's own
       * media only takes over once it reflects a deliberate choice rather than
       * a default. It also survives the post-add-to-cart refresh, which calls
       * update(false) and would otherwise be indistinguishable from a fresh
       * load.
       *
       * Supersedes the earlier hero-always patch, which blanked mediaId in
       * deriveState and therefore killed colour swapping entirely.
       *
       * To pin the hero permanently instead, drop the heroInitial check and
       * always call resetMedia(root). To restore stock behaviour, replace the
       * whole branch with: if (state.mediaId) activateMedia(root, state.mediaId);
       * else resetMedia(root);
       */
      // A reviewed product exception must also apply to its PDP and quick view.
      const heroInitial = root.hasAttribute('data-hero-initial') &&
        !root.hasAttribute('data-reviewed-variant-media');
      if (!heroInitial && state.mediaId) activateMedia(root, state.mediaId);
      else resetMedia(root);
      variantUrl = updateVariantUrl(productUrl, state.id);

      const detailsLink = root.querySelector('[data-full-details]');
      if (detailsLink) detailsLink.href = variantUrl;

      if (settings.updateHistory && global.history && global.location) {
        const historyUrl = updateVariantUrl(global.location.href, state.id);
        global.history.replaceState({}, '', historyUrl);
        variantUrl = historyUrl;
      }
      updateStructuredData(settings.structuredData, variant, variantUrl);
    } else {
      resetMedia(root);
      variantUrl = removeVariantUrl(productUrl);

      const detailsLink = root.querySelector('[data-full-details]');
      if (detailsLink) detailsLink.href = variantUrl;

      if (settings.updateHistory && global.history && global.location) {
        variantUrl = removeVariantUrl(global.location.href);
        global.history.replaceState({}, '', variantUrl);
      }
      clearStructuredData(settings.structuredData);
    }

    const status = root.querySelector('[data-variant-status]');
    if (settings.announce) {
      const message = state.valid
        ? [state.price, availabilityLabel].filter(Boolean).join('. ')
        : availabilityLabel || 'Currently unavailable';
      if (status) {
        status.textContent = '';
        global.requestAnimationFrame(() => {
          status.textContent = message;
        });
      } else if (global.sigAnnounce) {
        global.sigAnnounce(message);
      }
    }

    return state;
  }

  /* Availability-aware option pills.
     For each option value, considering the shopper's CURRENT other
     selections: does a variant exist with this value (else "impossible"),
     and is any such variant purchasable (else "soldout")? States are
     purely presentational - the radios stay operable and auto-resolve
     below keeps selections landing on a real variant. */
  function optionFieldsets(root) {
    return Array.from(root.querySelectorAll('[data-option-fieldset]'));
  }

  function currentSelections(root) {
    return Array.from(root.querySelectorAll('[data-option-input]:checked'))
      .map((input) => input.value);
  }

  function paintOptions(root, variants) {
    const fieldsets = optionFieldsets(root);
    if (!fieldsets.length || !variants.length) return;
    const selected = currentSelections(root);
    if (selected.length !== fieldsets.length) return;

    fieldsets.forEach((fieldset, position) => {
      fieldset.querySelectorAll('[data-option-input]').forEach((input) => {
        const matches = variants.filter((variant) => {
          const opts = variantOptions(variant);
          return opts.every((value, index) =>
            index === position ? value === input.value : value === selected[index]);
        });
        const exists = matches.length > 0;
        const available = matches.some((variant) => variant.available);
        const pill = input.closest('.option-pill');
        if (pill) {
          pill.classList.toggle('is-impossible', !exists);
          pill.classList.toggle('is-soldout', exists && !available);
        }
      });
      const echo = fieldset.querySelector('[data-opt-selected]');
      if (echo) echo.textContent = selected[position] || '';
    });
  }

  /* When a chosen value has no variant under the current other selections,
     move the OTHER options to the closest real variant that carries the
     chosen value - preferring purchasable variants, then maximum overlap
     with what the shopper already picked. The chosen value always wins. */
  function resolveSelections(root, variants, changedInput) {
    const fieldsets = optionFieldsets(root);
    if (!fieldsets.length || !variants.length || !changedInput) return;
    const selected = currentSelections(root);
    if (selected.length !== fieldsets.length) return;
    if (findVariant(variants, selected)) return;

    const changedFieldset = changedInput.closest('[data-option-fieldset]');
    const position = fieldsets.indexOf(changedFieldset);
    if (position < 0) return;

    const candidates = variants
      .filter((variant) => variantOptions(variant)[position] === changedInput.value)
      .map((variant) => {
        const opts = variantOptions(variant);
        const overlap = opts.reduce(
          (sum, value, index) => sum + (index !== position && value === selected[index] ? 1 : 0),
          0
        );
        return { variant, opts, score: (variant.available ? 100 : 0) + overlap };
      })
      .sort((a, b) => b.score - a.score);
    if (!candidates.length) return;

    const target = candidates[0].opts;
    fieldsets.forEach((fieldset, index) => {
      if (index === position) return;
      const input = Array.from(fieldset.querySelectorAll('[data-option-input]'))
        .find((candidate) => candidate.value === target[index]);
      if (input && !input.checked) input.checked = true;
    });
  }

  /* On first bind, never leave the shopper parked on a dead combination:
     if the checked radios describe a variant that doesn't exist or can't be
     bought while a purchasable one exists, snap every option to the first
     available variant. A product with zero purchasable variants keeps its
     honest sold-out state. */
  function snapToPurchasable(root, variants) {
    const fieldsets = optionFieldsets(root);
    if (!fieldsets.length || !variants.length) return;
    const selected = currentSelections(root);
    if (selected.length !== fieldsets.length) return;
    const exact = findVariant(variants, selected);
    if (exact && exact.available) return;
    const target = variants.find((variant) => variant.available) || (exact ? null : variants[0]);
    if (!target) return;
    const opts = variantOptions(target);
    fieldsets.forEach((fieldset, index) => {
      const input = Array.from(fieldset.querySelectorAll('[data-option-input]'))
        .find((candidate) => candidate.value === opts[index]);
      if (input && !input.checked) input.checked = true;
    });
  }

  function readVariants(root) {
    const data = root.querySelector('[data-variant-data]');
    if (!data) return [];
    try {
      const variants = JSON.parse(data.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      return [];
    }
  }

  function selectedVariant(root, variants) {
    const select = root.querySelector('[data-variant-select]');
    if (select) {
      return variants.find((variant) => String(variant.id) === String(select.value)) || null;
    }

    const optionInputs = Array.from(root.querySelectorAll('[data-option-input]:checked'));
    if (optionInputs.length) {
      return findVariant(variants, optionInputs.map((input) => input.value));
    }

    const idInput = root.querySelector('[data-variant-id]');
    return idInput
      ? variants.find((variant) => String(variant.id) === String(idInput.value)) || null
      : null;
  }

  function bind(root, options) {
    if (!root || root.hasAttribute('data-variant-bound')) return null;
    root.setAttribute('data-variant-bound', '');
    /* Hold the hero until the shopper deliberately picks an option. Cleared in
       the change handler below; read by applyVariant. */
    root.setAttribute('data-hero-initial', '');
    const variants = readVariants(root);
    const idInput = root.querySelector('[data-variant-id]');
    if (idInput && !idInput.hasAttribute('name')) idInput.setAttribute('name', 'id');
    const update = (announce) => {
      const state = applyVariant(root, selectedVariant(root, variants), {
        ...options,
        announce,
      });
      paintOptions(root, variants);
      return state;
    };

    root.querySelectorAll('[data-option-input], [data-variant-select]').forEach((control) => {
      control.addEventListener('change', (event) => {
        /* A real choice - variant media may lead from here on. */
        root.removeAttribute('data-hero-initial');
        if (control.hasAttribute('data-option-input')) {
          resolveSelections(root, variants, event.currentTarget);
        }
        update(true);
      });
    });
    snapToPurchasable(root, variants);
    update(false);
    return { update, variants };
  }

  global.SignatureVariantState = {
    activateMedia,
    applyVariant,
    bind,
    paintOptions,
    resolveSelections,
    snapToPurchasable,
    clearStructuredData,
    deriveState,
    findVariant,
    formatMoney,
    removeVariantUrl,
    resetMedia,
    updateStructuredData,
    updateVariantUrl,
  };
})(typeof window === 'undefined' ? globalThis : window);
